// Quick view, an episode: the first of the three views (Quick | Script | Timeline). The scenes as a row of storyboard panels to swipe through, with a blank "New scene" panel at
// the end, and under them the recording deck for the scene in view: who speaks, the speaker's face in each expression,
// two shots (everyone, or a close-up) either side of the big red button. Once you tap an expression or shot the panel shows it as it will be, and
// while you record the character's mouth moves with your voice. Each take lands at the end of that scene's lines,
// shown as chips under the panel (tap to hear, × to delete).
// Under the panel, a small round play button plays just that scene in its panel (picture, lip sync and sound, from the
// scene's start to its end; tap again to stop; swiping, recording or the full player stop it), then the take chips. A
// small × on the panel's top-right corner, half outside the frame, deletes the scene and its lines after a Confirm. The
// scene in view is whichever panel is nearest the middle, updated as you swipe, so the deck follows at once. Every
// height under the reel is fixed (the chips row, and a space as tall as the deck for every panel), so nothing resizes
// mid-swipe and the scroll never jumps. At rest the panel shows the scene exactly as it plays at its start; it only
// shows the picked expression and shot once you tap one, and while you record. The deck's shot starts as the one the
// scene's first line uses (Everyone when it has no lines). The blank "New scene" panel has a strip of earlier scenes under it: tap one to add a copy.
// Its top bar is plain: back, the title, an Options "⋯" and, in the far corner, a round play button that plays the whole
// episode full screen. Options is a bottom sheet with Undo, Redo, Export and "Advanced editor" (switches this episode to
// the Script view). TimelineEditor shows it, so loading, saving and undo are the usual ones; the episode is the same
// file the other views open. A visitor's demo episode keeps its own small top bar, with a "Watch" pill.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { engine } from "../../audio/playback";
import { resolveFraming, timelineFrameAt } from "../../engine/timelineFrame";
import { useLibrary, voiceOf } from "../../library/store";
import { EMOTIONS, type Emotion, type Viseme } from "../../model/face";
import { findAsset } from "../../model/library";
import {
  addMarker, cameraAt, canPlay, copyScene, feelingAt, lineById, linesUnder, marksOf, placeholderOf, removeLine, removeScene, spokenLines,
  type Framing, type PictureMark, type Placed, type PlacedLine, type TimelineEpisode,
} from "../../model/timeline";
import { FrameSvg } from "../../render/FrameSvg";
import { audioUrl } from "../api";
import { Avatar, Body, Confirm, Icon, Page, Toast, TopBar, cap, fmt, useIdleClock, useTimelinePlayback } from "../common";
import { useRecorder } from "../episode/Recorder";
import { useEditorPages, type View } from "../timeline/EditorShell";
import { FullPlayer } from "../timeline/FullPlayer";
import { useTimeline, type EditorProps } from "../timeline/TimelineLoader";
import { makeClip } from "../timeline/pages/makeClip";
import { facesFrame, frameWith } from "../timeline/pages/shared";
import { EarlierScenes } from "./Earlier";
import { NewScene } from "./NewScene";
import { Panel } from "./Panel";
import "./quick.css";
import "../timeline/episodeHead.css";
import "../landing/landing.css";

// A new episode opens straight into its first scene.
const fresh = new Set<string>();
export const startWithScene = (id: string) => void fresh.add(id);

type Shot = "all" | "close";
type Scene = Placed<PictureMark>;

// A rough mouth from the mic's level, so the character talks while you do.
const mouthFor = (level: number): Viseme => (level > 0.45 ? "D" : level > 0.22 ? "C" : level > 0.08 ? "B" : "X");

// `embedded`: the landing page's copy of the demo: no page, top bar or fixed footer, it just lays its reel, take chips,
// deck and Watch button out one under another inside whatever page holds it.
export function QuickView({ onBack, onOwn, onView, embedded }: EditorProps & { view?: View; onView?: (v: View) => void; embedded?: boolean }) {
  const { ep, update, undo, redo, canUndo, canRedo } = useTimeline();
  const demo = !!ep.demo || !!embedded; // the visitor's try-it episode: no new scenes, and "Make your own" in the top bar
  const lib = useLibrary();
  const clock = useIdleClock();
  const [making, setMaking] = useState(() => fresh.has(ep.id) && !ep.demo);
  const [playing, setPlaying] = useState(false);
  const [menu, setMenu] = useState(false); // the "⋯" sheet
  const [deleting, setDeleting] = useState(false); // asking about deleting the scene in view
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { fresh.delete(ep.id); }, [ep.id]);

  const scenes = marksOf(ep, "picture");
  const reel = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(Math.max(0, scenes.length - 1)); // the panel in view (nearest the middle, also while swiping); scenes.length is the blank one
  const scene: Scene | undefined = scenes[at];

  // Who speaks, how they look and the shot, for the scene in view. A new scene starts with whoever didn't speak last.
  const people = (scene?.faces ?? []).map(f => f.char).filter(c => findAsset(lib, c));
  const lines = scene ? linesUnder(ep, scene.id).filter(l => l.how !== "silent") : [];
  const [speaker, setSpeaker] = useState<string | undefined>(undefined);
  const [look, setLook] = useState<Emotion | null>(null); // null: however they look now
  const [shot, setShot] = useState<Shot>("close");
  const [picked, setPicked] = useState(false); // an expression or shot tile was tapped: the panel shows the pick, not just the scene
  const [hearing, setHearing] = useState<string | null>(null);
  useEffect(() => {
    const last = (lines.at(-1) ?? spokenLines(ep).at(-1))?.char;
    setSpeaker(people.find(c => c !== last) ?? people[0]);
    setLook(null);
    setPicked(false);
    if (scene) setShot(lines.length && cameraAt(ep, lines[0]!.start + 0.01).framing === "close" ? "close" : "all"); // (the shot its first line uses; Everyone for a scene with no lines)
  }, [scene?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const who = speaker && people.includes(speaker) ? speaker : people[0];
  const t = (scene?.t ?? 0) + 0.01;
  const emotion = look ?? (who && scene ? feelingAt(ep, who, scene.end - 0.01) : "neutral");
  const cameraOf = (s: Shot) => resolveFraming(ep, t, s === "close" && who ? "close" : "whole", s === "close" ? who : undefined);
  const framing: Framing = shot === "close" && who ? "close" : "whole";
  const camera = cameraOf(shot);

  const pb = useTimelinePlayback(ep, lib); // plays the scene in view
  const scenePlaying = pb.playing;
  const pages = useEditorPages(ep, () => engine.stop(), () => {}); // (only its export screen is used here)
  const recorder = useRecorder(take, setToast);
  const rec = recorder.rec;
  const level = rec.phase === "recording" ? recorder.levels.at(-1) ?? 0 : 0;

  // A scene added: bring it into view.
  const count = useRef(scenes.length);
  useLayoutEffect(() => {
    if (scenes.length > count.current) goTo(scenes.length - 1, false);
    else if (scenes.length < count.current) goTo(Math.min(at, demo ? scenes.length - 1 : scenes.length), false); // a scene deleted: what is now at its place
    count.current = scenes.length;
  }, [scenes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { goTo(at, false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function goTo(i: number, smooth = true) {
    const el = reel.current?.children[i] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: smooth ? "smooth" : "instant", inline: "center", block: "nearest" });
    setAt(i);
  }
  // Which panel is nearest the middle of the row.
  const nearest = useCallback(() => {
    const r = reel.current;
    if (!r) return null;
    const mid = r.scrollLeft + r.clientWidth / 2;
    let best = 0, gap = Infinity;
    [...r.children].forEach((c, i) => { const el = c as HTMLElement, d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid); if (d < gap) { gap = d; best = i; } });
    return best;
  }, []);
  // The scene in view follows the swipe at once.
  function onScroll() {
    const b = nearest();
    if (b !== null) setAt(b);
  }

  // A scene's playback stops when another panel comes into view, a recording starts or the full player opens.
  useEffect(() => { if (scenePlaying) pb.stop(); }, [at, recorder.busy, playing]); // eslint-disable-line react-hooks/exhaustive-deps
  function playScene() {
    if (!scene) return;
    if (pb.playing) return pb.stop();
    engine.stop();
    setHearing(null);
    void pb.play(scene.t, undefined, scene.end);
  }
  const addCopy = (pictureId: string) => update(e => copyScene(e, pictureId).ep); // (the reel then brings the new scene into view)
  function deleteScene() {
    if (!scene) return;
    pb.stop();
    engine.stop();
    setHearing(null);
    setDeleting(false);
    update(e => removeScene(e, scene.id));
  }

  // A take goes at the end of this scene's lines (or fills its untouched placeholder), with its expression and shot
  // set where it starts. Then it's the other person's turn.
  function take(blob: Blob, seconds: number) {
    if (!who || !scene) return;
    const ph = placeholderOf(ep, scene.id);
    const last = linesUnder(ep, scene.id).at(-1);
    const r = makeClip(ep, update, { char: who, t: last ? last.end - Math.min(0.01, last.duration / 4) : scene.t, ...(ph ? { editId: ph.id } : {}), source: { blob, seconds }, emotion, feeling: emotion });
    const start = ph?.start ?? r.start ?? 0;
    const pick = { framing, camera, ...(framing === "close" ? { on: who } : {}) };
    update(e => {
      const s = lineById(e, r.id)?.start ?? start;
      const withShot = addMarker(e, "camera", s, pick);
      return ph ? addMarker(withShot, "feeling", s, { char: who, emotion }) : withShot; // (a new line's feeling came with it)
    });
    setSpeaker(people.find(c => c !== who) ?? who);
    setLook(null);
    setPicked(false); // (the panel goes back to showing the scene itself, with its new line)
  }

  // One line's sound, through its speaker's voice; tapping it again stops it.
  async function hear(l: PlacedLine) {
    engine.stop();
    pb.stop();
    if (hearing === l.id || !l.audio) return setHearing(null);
    setHearing(l.id);
    try {
      const buf = await engine.load(audioUrl(ep.id, l.audio));
      await engine.playSample(buf, voiceOf(lib)(l.char ?? ""));
      setTimeout(() => setHearing(h => (h === l.id ? null : h)), buf.duration * 1000 + 300);
    } catch { setHearing(null); setToast("Couldn't play that line."); }
  }
  function remove(l: PlacedLine) {
    if (hearing === l.id) { engine.stop(); setHearing(null); }
    update(e => removeLine(e, l.id));
  }
  const close = () => { engine.stop(); if (demo) location.hash = "#/welcome"; else onBack(); }; // (a demo goes back to the landing page)

  // While you record, or after tapping an expression or shot: the scene as that shot will look, the picked expression on
  // the speaker, their mouth moving while recording. Otherwise the panel shows the scene as it plays at its start.
  const preview: TimelineEpisode = who ? addMarker(ep, "feeling", t, { char: who, emotion }) : ep;
  const showPick = picked || recorder.busy;
  const talk = rec.phase === "recording" && who ? { char: who, viseme: mouthFor(level) } : undefined;

  const [deckEl, setDeckEl] = useState<HTMLElement | null>(null);
  const [deckH, setDeckH] = useState(260); // the deck's height, so the space under the reel stays that tall on every panel
  useEffect(() => {
    if (!deckEl) return;
    const ro = new ResizeObserver(() => setDeckH(deckEl.offsetHeight));
    ro.observe(deckEl);
    return () => ro.disconnect();
  }, [deckEl]);
  const stillOn = !recorder.busy && !scenePlaying;

  const watch = <button className="sm-watch" disabled={!canPlay(ep) || recorder.busy} onClick={() => { engine.stop(); setPlaying(true); }}><Icon name="play" size={18} /> Watch</button>;
  const reelNode = (
    <div className={`sm-reel${recorder.busy ? " held" : ""}`} ref={reel} onScroll={onScroll}>
      {scenes.map((s, i) => (
        <div key={s.id} className={`sm-slide${i === at ? " on" : ""}`}>
          {i === at ? (
            <Panel rec={recorder.busy}>
              <FrameSvg id="sm-live" boil={false} frame={pb.t !== null ? timelineFrameAt(ep, pb.t, clock) : showPick ? facesFrame(preview, t, clock, s.picture, s.faces, camera, talk) : frameWith(ep, s.t + 0.01, 0)} />
              {rec.phase === "count" && <span className="sm-count" key={rec.n}>{rec.n}</span>}
              {rec.phase === "recording" && <span className="sm-rec-pill"><i /> {fmt(recorder.elapsed)}</span>}
            </Panel>
          ) : (
            <Panel onClick={() => goTo(i)} label={`Scene ${i + 1}`}>
              <FrameSvg id={`sm-reel-${i}`} boil={false} frame={frameWith(ep, s.t + 0.01, 0)} />
            </Panel>
          )}
          {i === at && stillOn && !demo && <button className="sm-scene-x" onClick={() => setDeleting(true)} aria-label="Delete this scene"><Icon name="close" size={16} /></button>}
        </div>
      ))}
      {!demo && (
        <div className={`sm-slide${at === scenes.length ? " on" : ""}`}>
          <Panel blank corners onClick={() => setMaking(true)} label="New scene">
            <span className="sm-blank-cta"><Icon name="plus" size={34} /><b>{scenes.length ? "New scene" : "Draw your first scene"}</b></span>
          </Panel>
        </div>
      )}
    </div>
  );
  const takesNode = (
    <div className="sm-takes" aria-label={scene ? `Scene ${at + 1} lines` : undefined}>
      {scene && <button className="sm-play" onClick={playScene} disabled={recorder.busy || !canPlay(ep)} aria-label={scenePlaying ? "Stop" : "Play this scene"}><Icon name={scenePlaying ? "stop" : "play"} size={16} /></button>}
      {scene && lines.map(l => <TakeChip key={l.id} ep={ep} line={l} hearing={hearing === l.id} onHear={() => void hear(l)} onRemove={() => remove(l)} onToast={setToast} />)}
    </div>
  );
  const deckNode = scene && who && (
    <div className={`sm-deck${recorder.busy ? " busy" : ""}`} ref={setDeckEl}>
      <p className="sm-hint">{lines.length ? "" : "Pick who's talking, then hit the red button"}</p>
      <div className="sm-speakers" role={people.length > 1 ? "radiogroup" : undefined} aria-label="Who's talking">
        {people.length > 1 && people.map(c => (
          <button key={c} role="radio" aria-checked={c === who} className={`sm-speaker${c === who ? " on" : ""}`} onClick={() => { setSpeaker(c); setLook(null); }}>
            <Avatar assetId={c} size={30} emotion={c === who ? emotion : "neutral"} />{findAsset(lib, c)?.name}
          </button>
        ))}
      </div>
      <div className="sm-moods" role="radiogroup" aria-label="Expression">
        {EMOTIONS.map(e => (
          <button key={e} role="radio" aria-checked={e === emotion} aria-label={cap(e)} title={cap(e)} className={`sm-mood${e === emotion ? " on" : ""}`} onClick={() => { setLook(e); setPicked(true); }}>
            <Avatar assetId={who} size={44} emotion={e} />
          </button>
        ))}
      </div>
      <div className="sm-shutter-row">
        <ShotTile ep={preview} t={t} label="Everyone" on={shot === "all"} camera={cameraOf("all")} onPick={() => { setShot("all"); setPicked(true); }} />
        <button className={`sm-shutter${rec.phase === "recording" ? " on" : ""}`} style={{ ["--lvl" as string]: level }}
          onClick={() => recorder.press()} aria-label={rec.phase === "idle" ? `Record ${findAsset(lib, who)?.name ?? ""}` : rec.phase === "recording" ? "Stop" : "Cancel"}><span /></button>
        <ShotTile ep={preview} t={t} label="Close-up" on={shot === "close"} camera={cameraOf("close")} onPick={() => { setShot("close"); setPicked(true); }} />
      </div>
    </div>
  );
  // The space under the reel for a panel with no deck: a scene with nobody's face in it, or the blank panel (a strip of earlier scenes).
  const noDeck = scene ? (
    <div className="sm-deck sm-hintonly" style={{ height: deckH }}><p className="sm-hint">Nobody's face is in this scene</p></div>
  ) : !demo && (
    <div className="sm-deck sm-reuse" style={{ height: deckH }}>
      {scenes.length ? (
        <>
          <p className="sm-hint">Or cut back to an earlier scene</p>
          <EarlierScenes ep={ep} onPick={addCopy} idPrefix="sm-again" />
        </>
      ) : <p className="sm-hint">Tap the panel to draw your first scene</p>}
    </div>
  );
  const extras = (
    <>
      {playing && <FullPlayer ep={ep} from={0} onClose={() => setPlaying(false)} />}
      {toast && <Toast onClick={() => setToast(null)}>{toast}</Toast>}
      {pages.node}
    </>
  );

  if (embedded) {
    return (
      <div className="sm sm-embed">
        {reelNode}
        {takesNode}
        {deckNode || noDeck}
        <div className="sm-embed-watch">{watch}</div>
        {extras}
      </div>
    );
  }
  return (
    <Page className={`sm sm-ep${demo ? " sm-demo" : ""}`}>
      {demo ? (
        <TopBar onBack={close} title={ep.title} right={<><button className="lp-own" onClick={() => { engine.stop(); onOwn?.(); }}>Make your own</button>{watch}</>} />
      ) : (
        <TopBar onBack={close} title={ep.title} right={<>
          <button className="icon-btn" onClick={() => setMenu(true)} aria-label="Options"><Icon name="more" size={22} /></button>
          <button className="sm-go" disabled={!canPlay(ep) || recorder.busy} onClick={() => { engine.stop(); pb.stop(); setPlaying(true); }} aria-label="Play the episode"><Icon name="play" size={20} /></button>
        </>} />
      )}
      <Body className="sm-ep-body">
        {reelNode}
        {takesNode}
      </Body>
      {demo ? (deckNode || noDeck) : (
        <div className="sm-under" style={{ minHeight: deckH }}>{deckNode || noDeck}</div>
      )}
      {menu && (
        <div className="sheet-wrap" onClick={() => setMenu(false)}>
          <div className="sheet" role="dialog" aria-label="Options" onClick={e => e.stopPropagation()}>
            <div className="sm-menu">
              <button className="btn big" disabled={!canUndo} onClick={() => { setMenu(false); undo(); }}><Icon name="undo" /> Undo</button>
              <button className="btn big" disabled={!canRedo} onClick={() => { setMenu(false); redo(); }}><Icon name="redo" /> Redo</button>
              <button className="btn big" disabled={!canPlay(ep)} onClick={() => { setMenu(false); pages.exportIt(); }}><Icon name="download" /> Export</button>
              {onView && <button className="btn big" onClick={() => { setMenu(false); engine.stop(); pb.stop(); onView("script"); }}><Icon name="edit" /> Advanced editor</button>}
            </div>
          </div>
        </div>
      )}
      {deleting && (
        <Confirm title="Delete this scene?" yes="Delete" danger onYes={deleteScene} onNo={() => setDeleting(false)}>
          Its recorded lines go too.
        </Confirm>
      )}
      {making && !demo && <NewScene onClose={() => setMaking(false)} />}
      {extras}
    </Page>
  );
}

// The landing page's editor: the same Quick view, laid out inline.
export function EmbeddedQuick({ onBack, onOwn }: EditorProps) { return <QuickView onBack={onBack} onOwn={onOwn} embedded />; }

function ShotTile({ ep, t, label, on, camera, onPick }: { ep: TimelineEpisode; t: number; label: string; on: boolean; camera: ReturnType<typeof resolveFraming>; onPick: () => void }) {
  return (
    <button className={`sm-shot${on ? " on" : ""}`} role="radio" aria-checked={on} onClick={onPick}>
      <span className="sm-shot-frame"><FrameSvg id={`sm-shot-${label}`} boil={false} frame={frameWith(ep, t, 0, camera)} /></span>
      {label}
    </button>
  );
}

// A line under the panel: who said it, and its length as a strip of sound. Tap to hear it; × deletes it.
function TakeChip({ ep, line: l, hearing, onHear, onRemove, onToast }: { ep: TimelineEpisode; line: PlacedLine; hearing: boolean; onHear: () => void; onRemove: () => void; onToast: (m: string) => void }) {
  const bad = l.status === "error";
  const busy = l.status === "syncing";
  const width = Math.round(Math.min(120, 22 + l.duration * 14));
  return (
    <span className={`sm-take${hearing ? " on" : ""}${bad ? " bad" : ""}${busy ? " busy" : ""}`}>
      <button className="sm-take-main" disabled={busy} aria-label={bad ? "Why this line didn't work" : hearing ? "Stop" : "Hear this line"}
        onClick={() => (bad ? onToast(`${l.error ?? "This line didn't work."} Delete it and record it again.`) : onHear())}>
        <Avatar assetId={l.char ?? ""} size={28} emotion={feelingAt(ep, l.char ?? "", l.start)} />
        <span className="sm-wave" style={{ width }} aria-hidden />
      </button>
      <button className="sm-take-x" onClick={onRemove} aria-label="Delete this line"><Icon name="close" size={14} /></button>
    </span>
  );
}
